import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function PotteryWorkshopsInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "pottery-workshops-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles ? articles.find((art) => art.slug === ref_id) : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Claymates Ceramics Studio",
    "section-2": "Vancouver Ceramics Studio",
    "section-3": "Café au Clay",
    "section-4": "HiDe Ceramic Works",
    "section-5": "OutForm Ceramics",
    "section-6": "Community Clay",
    "section-7": "Báhoo Studio",
    "section-8": "Studio Fundamentals",
    "section-9": "Hand Eye Ceramics",
    "section-10": "Mudlab Pottery",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/pottery-and-ceramics.aspx",
      label: "City of Vancouver – Pottery & Ceramics",
    },
    {
      url: "https://bcpotters.com/",
      label: "Potters Guild of British Columbia",
    },
    {
      url: "https://www.worksafebc.com/",
      label: "WorkSafeBC – Art Studio Safety Guidelines",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" matches the ref for the corresponding <Marker>.
  const locations = [
    {
      id: "section-1",
      lat: 49.27670,
      lng: -123.07730,
      popupText: "Claymates Ceramics Studio (East Vancouver)",
    },
    {
      id: "section-2",
      lat: 49.26800,
      lng: -123.10400,
      popupText: "Vancouver Ceramics Studio - Mount Pleasant",
    },
    {
      id: "section-2-alt",
      lat: 49.20750,
      lng: -123.13400,
      popupText: "Vancouver Ceramics Studio - Selkirk (Marpole)",
    },
    {
      id: "section-3",
      lat: 49.27050,
      lng: -123.14230,
      popupText: "Café au Clay (Kitsilano)",
    },
    {
      id: "section-4",
      lat: 49.26450,
      lng: -123.10880,
      popupText: "HiDe Ceramic Works (Mount Pleasant)",
    },
    {
      id: "section-5",
      lat: 49.21000,
      lng: -123.13950,
      popupText: "OutForm Ceramics (Marpole)",
    },
    {
      id: "section-6",
      lat: 49.26690,
      lng: -123.10450,
      popupText: "Community Clay (Olympic Village Area)",
    },
    {
      id: "section-7",
      lat: 49.28480,
      lng: -123.09500,
      popupText: "Báhoo Studio (Railtown)",
    },
    {
      id: "section-8",
      lat: 49.27870,
      lng: -123.07650,
      popupText: "Studio Fundamentals (East Vancouver)",
    },
    {
      id: "section-9",
      lat: 49.27950,
      lng: -123.09550,
      popupText: "Hand Eye Ceramics (Chinatown)",
    },
    {
      id: "section-9-alt",
      lat: 49.26600,
      lng: -123.09800,
      popupText: "Hand Eye Ceramics (Mount Pleasant)",
    },
    {
      id: "section-10",
      lat: 49.28110,
      lng: -123.06500,
      popupText: "Mudlab Pottery (Hastings-Sunrise)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Pottery Workshops in Vancouver</title>
        <meta
          name="description"
          content="Learn where to find pottery workshops in Vancouver, costs, and regulations for a perfect ceramics experience."
        />
        <meta
          name="keywords"
          content="pottery, ceramics, vancouver, workshops, clay"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/pottery-workshops-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Pottery Workshops in Vancouver"
        />
        <meta
          property="og:description"
          content="A one-stop guide to the best pottery studios, tips, and local regulations in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/pottery-workshops-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/pottery-workshops-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Pottery Workshops in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Discover top pottery studios, costs, and insider tips for ceramic enthusiasts in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/pottery-workshops-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "/assets/images/pottery-workshops-in-vancouver.jpg"}
              alt="Pottery Workshops in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s thriving arts scene makes pottery workshops a popular, hands-on escape
              for creatives of all levels. Whether you’re a beginner looking to get your hands
              muddy or a seasoned ceramicist seeking new studio space, pottery classes in Vancouver
              offer a fun way to unwind, learn new skills, and craft something unique. This guide
              explores the best pottery workshop locations across the city, approximate costs, and
              essential tips—ensuring you have all the info you need to start your clay journey.
            </p>

            {/* SECTION 1: Claymates Ceramics Studio */}
            <h2 id="section-1">Claymates Ceramics Studio</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Claymates Ceramics Studio</strong>{" "}
              <Marker
                coordY={49.27670}
                coordX={-123.07730}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A community-centric ceramics studio nestled near Strathcona Park in East Van. It
              offers multi-week classes, date-night workshops, and private events. Beginners are
              welcome, and the fun, supportive vibe makes this spot a perfect choice for first-timers
              or experienced potters alike. Single-session drop-ins start around <em>$25</em>, with
              multi-week courses typically in the <em>$335</em> range.
            </p>

            {/* SECTION 2: Vancouver Ceramics Studio */}
            <h2 id="section-2">Vancouver Ceramics Studio</h2>
            <p>
              Vancouver Ceramics Studio operates multiple locations, including Mount Pleasant and
              Selkirk in South Vancouver. With multi-week courses, drop-in lessons, and membership
              packages, it caters to beginners and advanced potters looking for a place to hone
              their skills.
            </p>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Mount Pleasant Studio</strong>{" "}
              <Marker
                coordY={49.26800}
                coordX={-123.10400}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A central hub with regular 6-week beginner classes and advanced workshops.
              Single-session introductory classes cost around <em>$50-$75</em>, while a 6-week
              program sits around <em>$280-$300</em>.
            </p>
            <p
              ref={(el) => setSpotRef("section-2-alt", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Selkirk Studio (Marpole)</strong>{" "}
              <Marker
                coordY={49.20750}
                coordX={-123.13400}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Located in South Vancouver, offering similar course options and membership access
              for more independent practice. 
            </p>

            {/* SECTION 3: Café au Clay */}
            <h2 id="section-3">Café au Clay</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Café au Clay</strong>{" "}
              <Marker
                coordY={49.27050}
                coordX={-123.14230}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A pottery studio in Kitsilano that merges ceramics with a cozy café. Ideal for a
              relaxed vibe, they offer drop-in pottery painting, beginner wheel classes, and
              memberships for those seeking regular studio access. Workshop prices start around
              <em>$45-$60</em>, with membership plans around <em>$150/month</em>.
            </p>

            {/* SECTION 4: HiDe Ceramic Works */}
            <h2 id="section-4">HiDe Ceramic Works</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>HiDe Ceramic Works</strong>{" "}
              <Marker
                coordY={49.26450}
                coordX={-123.10880}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Founded by a Japanese ceramic artist, this Mount Pleasant studio emphasizes
              craftsmanship and knowledge-sharing. Known for 80-minute drop-in wheel sessions,
              perfect for those with busy schedules. Prices range from about <em>$50-$60</em> per
              drop-in, or <em>$250-$400</em> for multi-week courses.
            </p>

            {/* SECTION 5: OutForm Ceramics */}
            <h2 id="section-5">OutForm Ceramics</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>OutForm Ceramics</strong>{" "}
              <Marker
                coordY={49.21000}
                coordX={-123.13950}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A cozy studio in Marpole, South Vancouver. They offer small group classes, private
              lessons, and monthly memberships. A one-day workshop is around <em>$80-$90</em>, while
              multi-week courses fall in the <em>$300-$350</em> range.
            </p>

            {/* SECTION 6: Community Clay */}
            <h2 id="section-6">Community Clay</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Community Clay</strong>{" "}
              <Marker
                coordY={49.26690}
                coordX={-123.10450}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A private artist-run space near Olympic Village. Small class sizes (max 8 students),
              8-week wheel-throwing courses, and a tranquil environment make it ideal for those
              seeking focused instruction. An 8-week course can cost <em>$400-$480</em>.
            </p>

            {/* SECTION 7: Báhoo Studio */}
            <h2 id="section-7">Báhoo Studio</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Báhoo Studio</strong>{" "}
              <Marker
                coordY={49.28480}
                coordX={-123.09500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A boutique, community-first ceramics space in Railtown. Woman- and queer-owned,
              Báhoo offers playful group workshops, date-night sessions, and small classes.
              Multi-week courses typically cost <em>$300-$360</em>; single sessions around
              <em>$60-$80</em>.
            </p>

            {/* SECTION 8: Studio Fundamentals */}
            <h2 id="section-8">Studio Fundamentals</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Studio Fundamentals</strong>{" "}
              <Marker
                coordY={49.27870}
                coordX={-123.07650}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located in East Vancouver, this loft-style studio places emphasis on solid technique
              and process-focused learning. A 6-week course is around <em>$335-$345</em>. They also
              host popular date-night workshops for <em>$195 per couple</em>.
            </p>

            {/* SECTION 9: Hand Eye Ceramics */}
            <h2 id="section-9">Hand Eye Ceramics</h2>
            <p>
              Hand Eye Ceramics features two Vancouver locations: Chinatown and Mount Pleasant. They
              run classes 7 days a week, catering to varied schedules with single-session workshops,
              multi-week classes, and memberships.
            </p>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Chinatown Studio</strong>{" "}
              <Marker
                coordY={49.27950}
                coordX={-123.09550}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – The primary location, where single workshop sessions are about <em>$90</em>, and
              6-week courses range <em>$300-$360</em>.
            </p>
            <p
              ref={(el) => setSpotRef("section-9-alt", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Mount Pleasant Studio</strong>{" "}
              <Marker
                coordY={49.26600}
                coordX={-123.09800}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Offers similar pricing and course structures as the Chinatown branch, plus membership
              options for experienced potters.
            </p>

            {/* SECTION 10: Mudlab Pottery */}
            <h2 id="section-10">Mudlab Pottery</h2>
            <p
              ref={(el) => setSpotRef("section-10", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Mudlab Pottery</strong>{" "}
              <Marker
                coordY={49.28110}
                coordX={-123.06500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – An East Vancouver studio along East Hastings, known for its friendly, low-pressure
              environment. Expect 4-to-6 week courses at <em>$280-$320</em> and one-off trial classes
              around <em>$70-$90</em>.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Pottery studios in Vancouver follow guidelines to keep participants safe. Kilns must
              meet permit requirements, and studios typically use proper ventilation to remove
              glaze fumes and clay dust. WorkSafeBC regulations encourage wet-cleaning to reduce
              silica dust. Food-safe, lead-free glazes are standard, especially for functional
              pottery. Studios often have age restrictions (16+ or 18+ for adult classes), and
              children’s classes are carefully supervised.
            </p>
            <p>
              For those setting up a home studio, it’s crucial to consult city bylaws on artist
              studio zoning and ensure correct ventilation. Gas kilns in particular require Technical
              Safety BC permits. Always adhere to official resources when handling materials or
              equipment.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need any experience or equipment to start?</h3>
              <p>
                Not at all. Most studios provide the tools, clay, and kilns. Just wear clothes you
                don’t mind getting messy. Beginners can jump right in.
              </p>

              <h3>2. How long does it take to finish a pottery piece?</h3>
              <p>
                Typically, you’ll create your piece in class, then leave it for 1-2 weeks to be
                bisque-fired and glazed before a second firing. Expect around 2–4 weeks total
                turnaround.
              </p>

              <h3>3. Are multi-week courses worth the cost?</h3>
              <p>
                Yes. Even though pottery can be pricier than some hobbies, you get equipment use,
                instruction, and finished pieces you keep. You also gain a new skill that can be
                therapeutic and creatively fulfilling.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you seek a peaceful solo activity or a fun group outing, Vancouver’s pottery
              workshops offer a hands-on creative escape. From the casual drop-in class to the
              in-depth 8-week course, there’s an option for everyone. Pick a studio that resonates
              with your budget and style, book a session, and get your hands dirty. You’ll walk
              away with new skills, fresh inspiration, and your own ceramic masterpiece. Happy
              potting!
            </p>
            <p>
              <strong>Disclaimer:</strong> Always check with each studio for the latest pricing,
              availability, and safety policies. Handle materials carefully, and follow official
              guidelines or contact local authorities for any concerns about equipment or
              regulations.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}